import { DemoConfig, DemoMarket, DemoTakerMarket, normalizePricingType } from '@/demo/demoConfig';
import {
  CreditNotesResponse,
  InvoiceRulesResponse,
  LockedInvoiceStatsResponse,
  LockedInvoicesResponse,
  MswData,
  OffersResponse,
  PlayableInvoiceStatsResponse,
  PlayableInvoicesResponse,
  PricingSuggestionsResponse,
  RecentClearsResponse,
  SEAMarketingArtifact,
  SupplierMarketClearsResponse,
  SupplierMarketClearsStatsResponse,
  SupplierMarketsResponse,
  UserSuppliersResponse,
  UserTermsResponse,
} from '@/test/mocks/sharedHandlers';

type SupplierMarketGroup = SupplierMarketsResponse['data']['supplierMarketGroups'][number];
type SupplierMarketEntry = SupplierMarketGroup['supplierMarkets'][number];

/**
 * Joins the demo config's market/takerMarket entities into the SEA `/supplier-markets` response
 * the dashboard fetches (useTakerMarkets). Stats come straight from each takerMarket entity — the
 * config app is the source of truth for invoice counts and eligible amounts.
 *
 * Each takerMarket becomes its own group, matching the API's `groupingKey: 'SUPPLIER_MARKET'`
 * (one supplier market per group; the group details omit the maker-org currency).
 */
export function buildSupplierMarkets(config: DemoConfig): SupplierMarketsResponse {
  const marketByUuid = new Map<string, DemoMarket>(config.markets.map((market) => [market.uuid, market]));
  const primaryBuyerOrgUuid = config.registrationConfig?.buyerMarketingConfig?.makerOrganizationUuid ?? null;
  const primaryBuyerLogoUrl = config.registrationConfig?.buyerMarketingConfig?.imageSvg ?? null;

  const supplierMarketGroups: SupplierMarketGroup[] = config.takerMarkets.map((takerMarket) => {
    const market = marketByUuid.get(takerMarket.marketUuid);
    const makerLogoPngUrl = takerMarket.makerOrganizationUuid === primaryBuyerOrgUuid ? primaryBuyerLogoUrl : null;
    const entry = buildSupplierMarketEntry(takerMarket, market, makerLogoPngUrl);

    // market?.type is an untrusted string from a URL-decoded config — normalize before treating it
    // as one of the supported pricing types.
    const marketType = normalizePricingType(market?.type);
    // Resolve EA rate with same fallback as buildPricingSuggestions: fall back to maxApr/maxDiscount
    // for old URL configs that predate the expressAcceptRate field.
    const resolvedEaRate = market?.expressAcceptRate ?? market?.maxApr ?? market?.maxDiscount;
    interface OfferOption {
      type: 'price-discovery' | 'static' | 'benchmark' | 'express-accept';
      frequency: 'recurring' | 'one-time';
    }
    const offerOptionsList: OfferOption[] =
      marketType === 'static'
        ? [{ type: 'static', frequency: 'recurring' }]
        : marketType === 'benchmark'
        ? [{ type: 'benchmark', frequency: 'recurring' }]
        : [{ type: 'price-discovery', frequency: 'recurring' }];
    if (marketType === 'price-discovery' && resolvedEaRate != null) {
      offerOptionsList.push({ type: 'express-accept', frequency: 'one-time' });
    }

    return {
      meta: {
        details: {
          makerOrganizationUuid: takerMarket.makerOrganizationUuid,
          makerDisplayName: takerMarket.makerOrganizationName,
          makerLogoPngUrl: takerMarket.makerOrganizationUuid === primaryBuyerOrgUuid ? primaryBuyerLogoUrl : null,
        },
        pricingType: marketType,
        isAllEnabledSupplierMarketsInOffer: false,
        offerDeadline: market?.nextClearDate ?? null,
        maxGoodFundsDate: null,
        offerOptions: {
          availableOffer: offerOptionsList,
          newOffer: offerOptionsList,
        },
      },
      supplierMarkets: [entry],
    };
  });

  return {
    data: {
      groupingKey: 'SUPPLIER_MARKET',
      supplierMarketGroups,
    },
  };
}

export function buildSupplierMarketEntry(
  takerMarket: DemoTakerMarket,
  market: DemoMarket | undefined,
  makerLogoPngUrl: string | null = null
): SupplierMarketEntry {
  return {
    supplier: {
      id: takerMarket.takerId,
      uuid: takerMarket.takerUuid,
      name: takerMarket.takerDivisionName,
      buyerId: takerMarket.takerMakerId,
      isInactive: false,
      lastInvoiceModificationDate: null,
      instantPay: null,
    },
    market: {
      id: takerMarket.marketId,
      uuid: takerMarket.marketUuid,
      type: market?.type ?? 'price-discovery',
      enabled: market?.isEnabled ?? true,
      currency: takerMarket.currency,
      marketingDisplayName: market?.name ?? takerMarket.makerOrganizationName,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
      makerDivisionId: takerMarket.makerId,
      makerDivisionName: takerMarket.makerDivisionName,
      makerLogoPngUrl,
      nextClearTime: market?.nextClearDate ?? null,
      goodFundsDate: null,
      payDate: market?.payDate ?? null,
      maxApr: market?.maxApr ?? null,
      maxDiscount: market?.maxDiscount ?? null,
      isPreferredEligible: false,
      isDsf: market?.isDsf ?? false,
      isTpf: false,
      isDiscountOnly: false,
      isInstantPayAllowed: false,
      lastClearDate: null,
    },
    offer: {
      isInOffer: false,
      offerType: null,
      offerStatus: null,
    },
    stats: {
      invoice: {
        eligible: {
          count: takerMarket.eligibleInvoiceCount,
          amount: takerMarket.eligibleInvoiceAmount,
          dpeWeightedSum: takerMarket.eligibleDpeWeightedSum,
          dpeWeightedAverage: takerMarket.eligibleDpeWeightedAverage,
        },
        instantPay: null,
        excluded: {
          count: takerMarket.takerExcludedInvoiceCount,
          amount: takerMarket.takerExcludedInvoiceAmount,
        },
      },
    },
  };
}

/**
 * Returns an empty offers list. Demo starts with no active offers so the supplier sees the
 * 'Name Your Rate' / offer builder UI — the canonical first-visit sales demo state.
 */
export function buildOffers(_config: DemoConfig): OffersResponse {
  return { data: [] };
}

/**
 * Builds the playable invoices response from the config's takerMarkets. Each takerMarket
 * contributes `eligibleInvoiceCount` synthetic invoices derived from its stats so the
 * invoice table count and total AR match the configured values.
 */
export function buildPlayableInvoices(config: DemoConfig): PlayableInvoicesResponse {
  const marketByUuid = new Map<string, DemoMarket>(config.markets.map((m) => [m.uuid, m]));
  const invoices: PlayableInvoicesResponse['data']['invoices'] = config.takerMarkets.flatMap((tm) => {
    const market = marketByUuid.get(tm.marketUuid);
    const count = tm.eligibleInvoiceCount;
    const total = Math.round(tm.eligibleInvoiceAmount);

    if (count === 0) return [];

    // Distribute total across invoices as varied integers that sum exactly to total.
    // Seed a simple LCG from the marketUuid so amounts are stable across refetches.
    const seed = tm.marketUuid.split('').reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 0);
    let rng = seed;
    const next = (): number => {
      rng = (rng * 1664525 + 1013904223) >>> 0;
      return rng;
    };

    // Assign each invoice a raw random weight, then scale to integers that sum to total.
    const weights = Array.from({ length: count }, () => (next() % 900) + 100);
    const weightSum = weights.reduce((a, b) => a + b, 0);
    const amounts = weights.map((w) => Math.floor((w / weightSum) * total));
    // Distribute rounding remainder to the first invoice.
    const remainder = total - amounts.reduce((a, b) => a + b, 0);
    amounts[0] += remainder;

    // originalDepositDate is the invoice's ORIGINAL due date — always after the market's payDate
    // (which the table renders as "Early Payment Sent"). DPE = originalDepositDate - payDate, i.e.
    // how many days earlier than the original due date the buyer pays. Spread deposit dates 1–89
    // days ahead of payDate so each invoice gets a unique, self-consistent DPE.
    const payDateMs = market?.payDate
      ? new Date(market.payDate).getTime()
      : market?.nextClearDate
      ? new Date(market.nextClearDate).getTime()
      : 1_750_000_000_000;
    const msPerDay = 24 * 60 * 60 * 1000;
    const toDateString = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

    return amounts.map((amount, i) => {
      // offsetDays 1–89 ensures DPE >= 1 and deposit date is strictly after payDate
      const offsetDays = (next() % 89) + 1;
      const depositMs = payDateMs + offsetDays * msPerDay;
      const depositDate = toDateString(depositMs);
      const daysPaidEarly = offsetDays;

      return {
        supplierDivisionUuid: tm.takerUuid,
        marketUuid: tm.marketUuid,
        invoiceId: null,
        voucherId: `DEMO-${tm.marketUuid}-${tm.takerUuid}-${i + 1}`,
        currency: tm.currency,
        originalAmount: amount,
        adjustmentAmount: null,
        amount,
        originalDepositDate: depositDate,
        daysPaidEarly,
        supplierExcluded: false,
        minDiscount: null,
        coversUnmatched: false,
        isAccepted: false,
        earn: null,
      };
    });
  });

  return {
    data: {
      invoices,
      pageInfo: { hasNextPage: false },
    },
  };
}

/**
 * Builds invoice statistics from the config's takerMarkets. Stats map directly from the
 * takerMarket entity fields — the config app is the authoritative source for these values.
 */
export function buildPlayableInvoiceStats(config: DemoConfig): PlayableInvoiceStatsResponse {
  const makerOrganizationUuid = config.takerMarkets[0]?.makerOrganizationUuid ?? '';

  const supplierMarkets: PlayableInvoiceStatsResponse['data']['supplierMarkets'] = config.takerMarkets.map((tm) => ({
    supplierDivisionUuid: tm.takerUuid,
    marketUuid: tm.marketUuid,
    currency: tm.currency,
    earliestEarlyPayDepositDate: null,
    playable: {
      count: tm.eligibleInvoiceCount,
      amount: tm.eligibleInvoiceAmount,
      dpeWeightedSum: tm.eligibleDpeWeightedSum,
      dpeWeightedAverage: tm.eligibleDpeWeightedAverage,
      earliestOriginalDepositDate: null,
    },
    offerable: {
      count: tm.eligibleInvoiceCount,
      amount: tm.eligibleInvoiceAmount,
      dpeWeightedSum: tm.eligibleDpeWeightedSum,
      dpeWeightedAverage: tm.eligibleDpeWeightedAverage,
      earliestOriginalDepositDate: null,
    },
  }));

  return { data: { makerOrganizationUuid, supplierMarkets } };
}

/**
 * Builds pricing suggestions from the config's takerMarkets.
 * - price-discovery: emits a price-discovery rate group (trending + optional express-accept)
 * - static: emits a static rate group using maxApr/maxDiscount as the fixed rate
 * - benchmark: emits nothing — benchmark markets show a read-only rate from market.maxApr/maxDiscount
 *   via RateDisplay and don't use the pricing-suggestions endpoint
 * When showPricing is false an empty response is returned — suppression is also enforced at the
 * feature-config level (SUPPRESS_PRICING_SUGGESTIONS).
 */
export function buildPricingSuggestions(config: DemoConfig): PricingSuggestionsResponse {
  const marketByUuid = new Map<string, DemoMarket>(config.markets.map((m) => [m.uuid, m]));

  const supplierMarketGroups: PricingSuggestionsResponse['data']['supplierMarketGroups'] = config.takerMarkets.flatMap(
    (tm) => {
      const market = marketByUuid.get(tm.marketUuid);
      const marketType = market?.type ?? 'price-discovery';
      const rateType = market?.rateType ?? 'APR';

      if (marketType === 'benchmark') {
        const referenceRate = market?.referenceRate ?? 0;
        const spread = market?.spread ?? 0;
        const totalRate = referenceRate + spread;
        const rateName = market?.rateName ?? '';
        if (!rateName) return [];
        return [
          {
            supplierMarkets: [{ marketUuid: tm.marketUuid, supplierDivisionUuid: tm.takerUuid }],
            rates: [
              {
                rateInformationType: 'benchmark' as const,
                rates: [
                  {
                    type: 'benchmark' as const,
                    rate: totalRate,
                    rateType,
                    referenceRate,
                    spread,
                    rateName,
                  },
                ],
              },
            ],
          },
        ];
      }

      if (marketType === 'static') {
        const fixedRate = rateType === 'DISC' ? market?.maxDiscount ?? 0 : market?.maxApr ?? 0;
        return [
          {
            supplierMarkets: [{ marketUuid: tm.marketUuid, supplierDivisionUuid: tm.takerUuid }],
            rates: [
              {
                rateInformationType: 'static' as const,
                rates: [{ type: 'static' as const, rate: fixedRate, rateType }],
              },
            ],
          },
        ];
      }

      // price-discovery — fall back to maxApr/maxDiscount for old URL configs that predate the trendingRate field
      const trendingRate = market?.trendingRate ?? (rateType === 'DISC' ? market?.maxDiscount : market?.maxApr);
      const expressAcceptRate = market?.expressAcceptRate ?? market?.maxApr ?? market?.maxDiscount;

      const rates: PricingSuggestionsResponse['data']['supplierMarketGroups'][number]['rates'] = [];

      if (trendingRate != null) {
        rates.push({
          rateInformationType: 'price-discovery',
          rates: [
            {
              type: 'price-discovery',
              pricingRateUuid: `demo-pricing-${tm.marketUuid}`,
              frequency: 'recurring',
              trendingRate,
              trendingRateStart: trendingRate * 0.95,
              trendingRateEnd: trendingRate * 1.05,
              rateType,
              showAverageDiscount: false,
              optimalRateType: rateType,
            },
          ],
        });
      }

      if (expressAcceptRate != null) {
        rates.push({
          rateInformationType: 'express-accept',
          rates: [
            {
              type: 'express-accept',
              pricingRateUuid: `demo-ea-${tm.marketUuid}`,
              frequency: 'one-time',
              trendingRate: expressAcceptRate,
              rateType,
              optimalRateType: rateType,
            },
          ],
        });
      }

      return [
        {
          supplierMarkets: [{ marketUuid: tm.marketUuid, supplierDivisionUuid: tm.takerUuid }],
          rates,
        },
      ];
    }
  );

  const overallOptimalRateType = config.markets[0]?.rateType ?? 'APR';
  return { data: { overallOptimalRateType, supplierMarketGroups } };
}

/**
 * Returns the supplier divisions associated with the demo user. The app uses this to determine
 * which suppliers to fetch market data for.
 */
export function buildUserSuppliers(config: DemoConfig): UserSuppliersResponse {
  const suppliers: UserSuppliersResponse['data']['suppliers'] = config.supplier.divisionUuids.map((uuid, i) => ({
    uuid,
    id: config.supplier.divisionIds?.[i] ?? i + 1,
    associationDate: '2020-01-01T00:00:00.000Z',
  }));

  return { data: { suppliers } };
}

/** Returns an empty terms list so the dashboard renders without showing any terms banners. */
export function buildUserTerms(_config: DemoConfig): UserTermsResponse {
  return { data: [] };
}

/**
 * Returns an empty invoice-rules list. Demo starts with no recurring exclusion rules configured,
 * matching the first-visit sales demo state.
 */
export function buildInvoiceRules(_config: DemoConfig): InvoiceRulesResponse {
  return { data: [] };
}

/**
 * Returns an empty clear history with zeroed pagination. Demo is a first-visit state with no prior
 * early-pay clears, so the Early Pay History widget renders its empty state.
 */
export function buildSupplierMarketClears(_config: DemoConfig): SupplierMarketClearsResponse {
  return {
    data: {
      groupingKey: 'SUPPLIER_MARKET',
      clears: [],
      pagination: { page: 1, limit: 25, totalCount: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false },
    },
  };
}

/**
 * Returns an empty locked-invoices list. Demo starts with no locked (adjusted/past-due/excluded)
 * invoices, matching the first-visit sales demo state — all configured invoices are eligible.
 */
export function buildLockedInvoices(_config: DemoConfig): LockedInvoicesResponse {
  return { data: { invoices: [], pageInfo: { hasNextPage: false } } };
}

/**
 * Returns zeroed locked-invoice statistics. Demo has no locked invoices, so per-market totals are
 * empty and the locked-invoice summary renders its empty state.
 */
export function buildLockedInvoiceStats(config: DemoConfig): LockedInvoiceStatsResponse {
  const makerOrganizationUuid = config.takerMarkets[0]?.makerOrganizationUuid ?? '';
  return { data: { makerOrganizationUuid, supplierMarkets: [] } };
}

/**
 * Returns an empty credit-notes list. Demo starts with no buyer credit-note templates configured,
 * matching the first-visit sales demo state.
 */
export function buildCreditNotes(_config: DemoConfig): CreditNotesResponse {
  return { data: [] };
}

/**
 * Returns an empty recent-clears list. Demo is a first-visit state with no prior early-pay clears,
 * so the Recent Awards widget renders its empty state.
 */
export function buildRecentClears(_config: DemoConfig): RecentClearsResponse {
  return { data: { clears: [] } };
}

/**
 * Returns zeroed clear-history stats. Demo has no prior clears, so the history totals render as zero.
 */
export function buildSupplierMarketClearsStats(config: DemoConfig): SupplierMarketClearsStatsResponse {
  return {
    data: {
      clearedInvoiceAmount: 0,
      clearedEarn: 0,
      clearedDiscountWeightedAvg: 0,
      currency: config.supplier.currency,
      minimumClearPayDate: '',
      minimumClearDepositDate: '',
      latestClearPayDate: '',
      latestClearDepositDate: '',
    },
  };
}

/**
 * Builds the GetMe `me` object from the demo config supplier and its organization, so the dashboard
 * renders the configured user identity. Field layout mirrors the GetMe GraphQL selection set.
 */
export function buildDemoSupplier(config: DemoConfig): MswData['supplier'] {
  const { supplier } = config;
  const organization = config.organizations.find((org) => org.id === supplier.organizationId);

  return {
    account: {
      id: supplier.organizationUuid,
      name: organization?.name ?? `${supplier.firstName} ${supplier.lastName}`,
      newExperienceEnabled: true,
      segment: null,
      srm: supplier.srm
        ? {
            calendarLink: null,
            center: null,
            emailAddress: supplier.srm.email,
            fallback: false,
            firstName: supplier.srm.firstName,
            isCalendlyEnabled: false,
            lastName: supplier.srm.lastName,
            phoneNumber: supplier.srm.phone ?? null,
            team: null,
          }
        : null,
    },
    created: '2024-01-01T00:00:00.000Z',
    divisions: [],
    emailAddress: supplier.email,
    formatPreferences: {
      aggregateCurrencyFormat: null,
      dateDisplayFormat: null,
      numberDisplayFormat: null,
      useCustomFormatting: false,
    },
    intercomUserId: null,
    loginHistory: [],
    personalInfo: {
      address: { country: organization?.country ?? null, postalCode: null },
      arDepartmentEmailAddress: null,
      firstName: supplier.firstName,
      fiscalYearEnd: null,
      industry: organization?.industry ?? null,
      lastName: supplier.lastName,
      locale: supplier.language,
      primaryPhoneNumber: null,
      role: null,
      secondaryPhoneNumber: null,
    },
    supplierReadOnly: false,
  };
}

const DEMO_MARKETING_ARTIFACT_CONTENT: Record<
  'how-to' | 'quarter-end' | 'preferred-offer',
  Extract<NonNullable<SEAMarketingArtifact['localizedContent']>[string], { title: string }>
> = {
  'how-to': {
    title: 'How it works',
    body: 'Set your offer, get it evaluated, and receive payment early — all in a few clicks.',
    bodyFormat: 'PLAIN',
    primaryCta: { text: 'Learn More', action: 'NAVIGATE', target: 'customers' },
    icon: { key: 'LightBulbGearIcon' },
  },
  'quarter-end': {
    title: 'Quarter-end opportunity',
    body: 'You have eligible invoices ready for early payment. Submit your offer before the market closes.',
    bodyFormat: 'PLAIN',
    primaryCta: { text: 'Request Early Payment', action: 'NAVIGATE', target: 'customers' },
    icon: { key: 'ClipboardClockIcon' },
  },
  'preferred-offer': {
    title: 'Preferred Offer available',
    body: "You've been pre-approved for a Preferred Offer. Lock in a great rate and get paid early.",
    bodyFormat: 'PLAIN',
    primaryCta: { text: 'View Offer', action: 'NAVIGATE', target: 'customers' },
    icon: { key: 'HandshakeIcon' },
  },
};

/**
 * Builds the MARKETING_MESSAGE artifact array seeded into demo MSW data.
 * The widget's artifact path takes priority over client-flag fallback, so this deterministically
 * controls which marketing widget the ent demo renders regardless of Split treatments or buyer
 * eligibility. 'none' aliases to 'how-to' (widget always renders something).
 */
export function buildMarketingArtifacts(config: DemoConfig): SEAMarketingArtifact[] {
  const slug = config.config?.marketingWidget;
  const resolvedSlug = slug === 'none' || slug === undefined ? 'how-to' : slug;
  const content = DEMO_MARKETING_ARTIFACT_CONTENT[resolvedSlug as keyof typeof DEMO_MARKETING_ARTIFACT_CONTENT];

  if (!content) {
    return [];
  }

  return [
    {
      uuid: `demo-marketing-artifact-${resolvedSlug}`,
      campaignUuid: 'demo-campaign-uuid',
      authServiceUuid: config.supplier.uuid,
      accountId: `demo-account-${config.supplier.organizationId}`,
      artifactType: 'MARKETING_MESSAGE',
      placements: ['early-pay.home.side-card'],
      priority: 1,
      localizedContent: { 'en-US': content },
      display: { dismissible: false, showOnce: false },
      locale: 'en-US',
      tracking: {
        artifactUuid: `demo-marketing-artifact-${resolvedSlug}`,
        assetUuid: `demo-asset-${resolvedSlug}`,
        campaignUuid: 'demo-campaign-uuid',
        campaignRunUuid: 'demo-campaign-run-uuid',
      },
      expiresAt: null,
    },
  ];
}
